<script lang="ts">
	// T35 Cash movement (UC-67), over Shift (T29): cash taken out of the
	// drawer or put back, with how much and why. Out or In picks the way;
	// the amount comes from the app keypad in a box that starts empty, as on
	// Open shift (T30); "Why?" takes a typed reason on the OS keyboard. Confirm
	// records it, and the drawer opens through the print queue, out and in
	// alike. A movement takes no passcode, as a no-sale open does (UX-7).
	// The reason is Other, with "Why?", or Driver float (UC-67.R4): the shop's
	// active drivers as one-tap name tiles, none picked, in place of the note,
	// as Pick a driver (T33) shows them. The store answers every rule; the
	// sheet only says its refusal in cashier words.

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Field from '../components/Field.svelte';
	import Keypad from '../components/Keypad.svelte';
	import Loading from '../components/Loading.svelte';
	import NameTile from '../components/NameTile.svelte';
	import Segmented from '../components/Segmented.svelte';
	import Sheet from '../components/Sheet.svelte';
	import { formatMinor, parseTypedMinor } from '../format/money';
	import { message, type Language } from '../i18n/messages';
	import {
		cashMovementAttempt,
		floatDrivers,
		MAX_NOTE,
		type CashDirection,
		type CashReason,
		type Driver,
	} from '../till/cashMovement.svelte';
	import { typeTender } from './Payment.svelte';

	interface Props {
		open: boolean;
		language: Language;
		minorDigits: number;
		/**
		 * Told whether an amount or a reason is typed, kept while the sheet is
		 * closed, or a movement is on its way: the till's own lock installs no
		 * app update over it (#925).
		 */
		onHolding?: (holding: boolean) => void;
		onClose: () => void;
	}

	let { open, language, minorDigits, onHolding, onClose }: Props = $props();

	let direction = $state<CashDirection>('out');
	let typed = $state('');
	let note = $state('');
	let reason = $state<CashReason>('other');
	let driverId = $state('');
	// The shop's drivers as the store last answered them: null while the
	// first read runs, and failed when the store did not answer.
	let drivers = $state<Driver[] | null>(null);
	let driversFailed = $state(false);
	// Each read takes the next number, and only the newest read's answer is
	// kept, so an older list that comes back later never offers a driver a
	// newer one left out.
	let driversRead = 0;
	let busy = $state(false);
	let slow = $state(false);
	let failure = $state('');
	let send = cashMovementAttempt();

	$effect(() => {
		onHolding?.(typed !== '' || note !== '' || driverId !== '' || busy);
	});

	const amountMinor = $derived(parseTypedMinor(typed));
	const ready = $derived(
		amountMinor !== null && (reason === 'other' ? note.trim() !== '' : driverId !== ''),
	);

	async function loadDrivers() {
		driversFailed = false;
		const mine = ++driversRead;
		const read = await floatDrivers();
		if (mine !== driversRead) return;
		if (read === null) {
			driversFailed = drivers === null;
			return;
		}
		drivers = read;
		// A driver the newest list no longer names is not offered, so he is
		// no longer picked.
		if (!read.some((d) => d.id === driverId)) driverId = '';
	}

	function pickReason(value: string) {
		reason = value === 'driver_float' ? 'driver_float' : 'other';
		// A pick never outlives its reason: Driver float opens with none.
		driverId = '';
		if (reason === 'driver_float') void loadDrivers();
	}
	const title = $derived(message(language, direction === 'out' ? 'shiftCashOut' : 'shiftCashIn'));

	function done() {
		direction = 'out';
		typed = '';
		note = '';
		reason = 'other';
		driverId = '';
		failure = '';
		send = cashMovementAttempt();
		onClose();
	}

	async function confirm() {
		if (amountMinor === null || !ready || busy) return;
		busy = true;
		failure = '';
		const timer = setTimeout(() => (slow = true), 300);
		try {
			const refused = await send(
				language,
				reason === 'other'
					? { direction, amountMinor, reason, note }
					: { direction, amountMinor, reason, note: '', driverId },
			);
			if (refused === null) {
				done();
				return;
			}
			failure = refused;
			// A driver the store does not hold, or one removed, comes off the
			// list read again; with no answer the list stays as it was.
			if (reason === 'driver_float') void loadDrivers();
		} finally {
			clearTimeout(timer);
			busy = false;
			slow = false;
		}
	}
</script>

<Sheet
	{open}
	{title}
	onClose={() => !busy && onClose()}
	closeLabel={message(language, 'sheetClose')}
	closeTestId="cash-move-close"
	testId="panel-cash-move"
	primaryLabel={message(language, 'confirm')}
	primaryIcon="check"
	primaryTestId="cash-move-confirm"
	primaryDisabled={!ready || busy}
	primaryState={slow ? 'pending' : 'idle'}
	pendingLabel={message(language, 'cashMoveRecording')}
	onPrimary={() => void confirm()}
	locked={busy}
>
	<!-- The way and the reason beside the keypad, as on Open shift, so the
	     whole pad fits a 1024x600 till. -->
	<div class="cash-move">
		<div class="ask">
			<Segmented
				label={message(language, 'shiftCashOutIn')}
				testId="cash-move-direction"
				wide
				value={direction}
				options={[
					{ value: 'out', label: message(language, 'shiftCashOut'), testId: 'cash-move-out' },
					{ value: 'in', label: message(language, 'shiftCashIn'), testId: 'cash-move-in' },
				]}
				onChange={(value) => (direction = value === 'in' ? 'in' : 'out')}
			/>
			{#if failure}
				<Band tone="flag" testId="cash-move-error">{failure}</Band>
			{/if}
			<div dir="ltr">
				<Field
					testId="cash-move-amount"
					label={message(language, 'cashMoveAmount')}
					value={amountMinor === null ? '–' : formatMinor(amountMinor, minorDigits)}
					mono
				/>
			</div>
			<Segmented
				label={message(language, 'cashMoveReason')}
				testId="cash-move-reason"
				wide
				value={reason}
				options={[
					{ value: 'other', label: message(language, 'cashMoveOther'), testId: 'cash-move-other' },
					{
						value: 'driver_float',
						label: message(language, 'cashMoveDriverFloat'),
						testId: 'cash-move-driver-float',
					},
				]}
				onChange={pickReason}
			/>
			{#if reason === 'other'}
				<Field
					testId="cash-move-note"
					label={message(language, 'cashMoveWhy')}
					value={note}
					maxCharacters={MAX_NOTE}
					onInput={(value) => (note = value)}
				/>
			{:else if drivers === null && !driversFailed}
				<div class="wait">
					<Loading label={message(language, 'cashMoveDriver')} size="sm" />
				</div>
			{:else if drivers === null}
				<div class="failed">
					<p class="said" data-testid="cash-move-drivers-failed">
						{message(language, 'cashMoveDriversFailed')}
					</p>
					<Button
						testId="cash-move-drivers-retry"
						label={message(language, 'retry')}
						tone="secondary"
						size="md"
						onPress={() => void loadDrivers()}
					/>
				</div>
			{:else if drivers.length === 0}
				<p class="said" data-testid="cash-move-no-drivers">
					{message(language, 'cashMoveNoDrivers')}
				</p>
			{:else}
				<ul
					class="drivers"
					role="list"
					aria-label={message(language, 'cashMoveDriver')}
					data-testid="cash-move-drivers"
				>
					{#each drivers as driver (driver.id)}
						<NameTile
							testId="cash-move-driver-{driver.id}"
							name={driver.name}
							chosen={driver.id === driverId}
							onPick={() => (driverId = driver.id)}
						/>
					{/each}
				</ul>
			{/if}
		</div>
		<div class="pad">
			<Keypad
				testId="cash-move-pad"
				label={message(language, 'cashMoveAmount')}
				deleteLabel={message(language, 'backspace')}
				disabled={busy}
				canType={typeTender(typed, '0') !== null}
				canDelete={typed.length > 0}
				onKey={(key) => {
					const next = typeTender(typed, key);
					if (next !== null) typed = next;
				}}
				onBackspace={() => (typed = typed.slice(0, -1))}
			/>
		</div>
	</div>
</Sheet>

<style>
	.cash-move {
		display: flex;
		gap: var(--space-9);
	}

	.ask {
		flex: 1;
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.pad {
		flex: none;
		align-self: flex-start;
	}

	.wait {
		display: grid;
		place-items: center;
		min-block-size: var(--name-h);
	}

	.failed {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-5);
	}

	.said {
		margin: 0;
		font-size: var(--text-base);
		font-weight: 600;
		color: var(--text-strong);
	}

	.drivers {
		margin: 0;
		padding: 0;
		list-style: none;
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(var(--key-w), 1fr));
		gap: var(--touch-gap);
		max-block-size: calc(2 * var(--name-h) + var(--touch-gap));
		overflow-y: auto;
	}
</style>
